import { useState, useEffect, useCallback } from 'react';
import { NavigateFunction } from 'react-router-dom';

// Ghost API response types (updated with meta fields)
export interface GhostImage {
  url: string;
  alt?: string;
}

export interface GhostAuthor {
  id: string;
  name: string;
  slug: string;
  profile_image?: string;
}

export interface GhostTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  feature_image?: string;
  visibility: string;
  meta_title?: string;
  meta_description?: string;
}

export interface GhostPost {
  id: string;
  uuid: string;
  title: string;
  slug: string;
  html: string;
  excerpt: string;
  feature_image: string;
  featured: boolean;
  published_at: string;
  updated_at: string;
  url: string;
  reading_time: number;
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  og_title?: string;
  og_description?: string;
  twitter_image?: string;
  twitter_title?: string;
  twitter_description?: string;
  primary_tag?: GhostTag;
  tags: GhostTag[];
  authors: GhostAuthor[];
}

export interface GhostPostsResponse {
  posts: GhostPost[];
  meta: {
    pagination: {
      page: number;
      limit: number;
      pages: number;
      total: number;
      next?: number;
      prev?: number;
    };
  };
}

const PER_PAGE = 6;

const API_URL = import.meta.env.VITE_GHOST_API_URL as string;
const API_KEY = import.meta.env.VITE_GHOST_API_KEY as string;

if (!API_URL || !API_KEY) {
  console.warn(
    '[useBlog] VITE_GHOST_API_URL and/or VITE_GHOST_API_KEY is missing'
  );
}

// Simple fetch wrapper
const fetchFromGhost = async (
  endpoint: string,
  params: Record<string, any>
) => {
  const url = new URL(`${API_URL}/ghost/api/content/${endpoint}`);
  params.key = API_KEY;
  url.search = new URLSearchParams(params).toString();

  const res = await fetch(url.toString(), {
    headers: { 'Accept-Encoding': 'gzip' },
  });

  if (!res.ok) {
    throw new Error(`Fetch error: ${res.statusText}`);
  }

  return res.json();
};

interface UseBlogProps {
  slug?: string;
  navigate: NavigateFunction;
}

export const useBlog = ({ slug, navigate }: UseBlogProps) => {
  const [posts, setPosts] = useState<GhostPost[]>([]);
  const [tags, setTags] = useState<GhostTag[]>([]);
  const [selectedTagSlug, setSelectedTagSlug] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<GhostPost | null>(null);

  // Updated pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingSlug, setLoadingSlug] = useState(false);
  const [loadingTagId, setLoadingTagId] = useState<string | 'all' | null>(null);
  const [loadingPostId, setLoadingPostId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stripHtml = useCallback((html: string) => {
    const tmp = new DOMParser().parseFromString(html, 'text/html');
    return tmp.body.textContent || '';
  }, []);

  const formatDate = useCallback(
    (dateStr: string) =>
      new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    []
  );

  const calcReadTime = useCallback(
    (content: string) => {
      const words = stripHtml(content).split(/\s+/).length;
      return Math.max(1, Math.ceil(words / 200));
    },
    [stripHtml]
  );

  const getFeaturedImageUrl = useCallback(
    (post: GhostPost) => post.feature_image || '',
    []
  );

  const getTagName = useCallback(
    (id: string) => tags.find((t) => t.id === id)?.name || '',
    [tags]
  );

  // New helper functions for meta data
  const getMetaTitle = useCallback((post: GhostPost) => {
    return post.meta_title || post.og_title || post.title;
  }, []);

  const getMetaDescription = useCallback((post: GhostPost) => {
    return post.meta_description || post.og_description || post.excerpt || stripHtml(post.html).substring(0, 160);
  }, [stripHtml]);

  const getMetaImage = useCallback((post: GhostPost) => {
    return post.og_image || post.twitter_image || post.feature_image || '';
  }, []);

  // Updated fetchPosts function for pagination
  const fetchPosts = useCallback(
    async (page = 1, tagOverride: string | null = selectedTagSlug) => {
      setLoadingPosts(true);
      try {
        const params: Record<string, any> = {
          limit: PER_PAGE,
          page,
          include: 'tags,authors',
          formats: 'plaintext',
          // Include meta fields in the response
          fields: 'id,uuid,title,slug,html,excerpt,feature_image,featured,published_at,updated_at,url,reading_time,meta_title,meta_description,og_image,og_title,og_description,twitter_image,twitter_title,twitter_description'
        };
        if (tagOverride) params.filter = `tag:${tagOverride}`;

        const data: GhostPostsResponse = await fetchFromGhost('posts', params);
        setPosts(data.posts);
        setCurrentPage(data.meta.pagination.page);
        setTotalPages(data.meta.pagination.pages);
        setTotalPosts(data.meta.pagination.total);
      } catch (err) {
        console.error('Failed to fetch blog posts:', err);
        setError('Failed to fetch blog posts.');
      } finally {
        setLoadingPosts(false);
        setLoadingTagId(null);
      }
    },
    [selectedTagSlug]
  );

  const fetchFullPostBySlug = useCallback(
    async (postSlug: string) => {
      setLoadingSlug(true);
      try {
        const data = await fetchFromGhost('posts', {
          filter: `slug:${postSlug}`,
          include: 'tags,authors',
          formats: 'html',
          // Include all meta fields for SEO
          fields: 'id,uuid,title,slug,html,excerpt,feature_image,featured,published_at,updated_at,url,reading_time,meta_title,meta_description,og_image,og_title,og_description,twitter_image,twitter_title,twitter_description'
        });

        if (data.posts.length) {
          setSelectedPost(data.posts[0]);
        } else {
          navigate('/blog');
        }
      } catch (err) {
        console.error('Failed to load the blog post:', err);
        setError('Failed to load the blog post.');
      } finally {
        setLoadingSlug(false);
        setLoadingPostId(null);
      }
    },
    [navigate]
  );

  const fetchTags = useCallback(async () => {
    try {
      const data = await fetchFromGhost('tags', {
        limit: 'all',
        include: 'count.posts',
        // Include meta fields for tags as well
        fields: 'id,name,slug,description,feature_image,visibility,meta_title,meta_description'
      });
      setTags(data.tags.filter((t: GhostTag) => t.visibility !== 'internal'));
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      setError('Failed to load blog tags.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await fetchTags();
        fetchPosts(1);
      } catch {
        setError('Failed to load blog data.');
      }
    })();
  }, [fetchPosts, fetchTags]);

  useEffect(() => {
    if (slug) {
      fetchFullPostBySlug(slug);
    } else {
      setSelectedPost(null);
    }
  }, [slug, fetchFullPostBySlug]);

  return {
    posts,
    tags,
    selectedTagSlug,
    selectedPost,
    
    // Updated pagination info
    currentPage,
    totalPages,
    totalPosts,
    
    error,
    loadingPosts,
    loadingSlug,
    loadingTagId,
    loadingPostId,

    formatDate,
    calcReadTime,
    getFeaturedImageUrl,
    stripHtml,
    getTagName,
    getMetaTitle,
    getMetaDescription,
    getMetaImage,

    handleTagSelect: (tagSlug: string | null, btnId: string | 'all') => {
      if (loadingTagId) return;
      setLoadingTagId(btnId);
      setSelectedTagSlug(tagSlug);
      setCurrentPage(1); // Reset to first page when changing tags
      fetchPosts(1, tagSlug);
    },

    // New pagination handlers
    handlePageChange: (page: number) => {
      if (page >= 1 && page <= totalPages && !loadingPosts) {
        fetchPosts(page, selectedTagSlug);
        // Scroll to top when changing pages
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    handleViewPost: (post: GhostPost) => {
      setLoadingPostId(post.id);
      const tag = post.primary_tag?.slug || post.tags[0]?.slug || 'uncategorized';
      navigate(`/blog/${tag}/${post.slug}`);
    },

    handleBack: () => {
      setSelectedPost(null);
      setLoadingPostId(null);
      navigate('/blog');
    },
  };
};